import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { SwitchWithLabel } from "@/components/form/SwitchWithLabels";
import { HubFormItem } from "@/components/inputs/HubFormItem";
import { RiskBandsArchetypeSelector } from "@/components/layouts/RiskBandsArchetypeSelector";
import { RiskBandsBuildYearSelector } from "@/components/layouts/RiskBandsBuildYearSelector";
import { RiskBandsHeightAboveGroundSelector } from "@/components/layouts/RiskBandsHeightAboveGroundSelector";
import { RiskBandsAdvancedSettingsValues, RiskBandsArchetype } from "@/tools/risk-bands/types";
import { Col, Collapse, Form, Row } from "antd";
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
interface RiskBandsAdvancedSettingsProps {
  notice?: string;
  setRefs?: (refs: RiskBandsAdvancedSettingsRefs) => void;
  values: RiskBandsAdvancedSettingsValues;
  setValues: Dispatch<SetStateAction<RiskBandsAdvancedSettingsValues>>;
  isAdvancedOpen: boolean;
}
export interface RiskBandsAdvancedSettingsRefs {
  archetype: MutableRefObject<null>;
  buildYear: MutableRefObject<null>;
  heightAboveGround: MutableRefObject<null>;
  advancedCollapse: MutableRefObject<null>;
  anomalyManagementRp: MutableRefObject<null>;
  defences: MutableRefObject<null>;
}
type AdvancedSettingsKeys = "advanced-settings" | "";
export const RiskBandsAdvancedSettings = ({
  notice,
  values,
  setRefs,
  setValues,
  isAdvancedOpen
}: RiskBandsAdvancedSettingsProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const {
    t: terms
  } = useTranslation("terms");
  const archetypeRef = useRef(null);
  const buildYearRef = useRef(null);
  const heightAboveGroundRef = useRef(null);
  const anomalyManagementRpRef = useRef(null);
  const defencesRef = useRef(null);
  const advancedCollapseRef = useRef(null);
  const [advancedActiveKey, setAdvancedActiveKey] = useState<AdvancedSettingsKeys>();
  useEffect(() => {
    setAdvancedActiveKey(isAdvancedOpen ? "advanced-settings" : "");
  }, [isAdvancedOpen]);
  const toggleAdvancedOpen = (activeKeys: string[]) => {
    setAdvancedActiveKey(activeKeys.includes("advanced-settings") ? "advanced-settings" : "");
  };
  const handleArchetypeChange = (value: RiskBandsArchetype) => {
    setValues(v => ({
      ...v,
      archetype: value
    }));
  };
  const handleBuildYearChange = (source: string, amount: number) => {
    setValues(v => ({
      ...v,
      buildYear: {
        source,
        amount
      }
    }));
  };
  const handleHeightAboveGroundChange = (source: string, amount: number) => {
    setValues(v => ({
      ...v,
      heightAboveGround: {
        source,
        amount
      }
    }));
  };
  useEffect(() => {
    if (!setRefs) {
      return;
    }
    setRefs({
      archetype: archetypeRef,
      buildYear: buildYearRef,
      heightAboveGround: heightAboveGroundRef,
      anomalyManagementRp: anomalyManagementRpRef,
      defences: defencesRef,
      advancedCollapse: advancedCollapseRef
    });
  }, [archetypeRef, buildYearRef, heightAboveGroundRef, anomalyManagementRpRef, defencesRef]);
  return <Form layout="vertical" className="[&_.ant-form-item-additional]:!min-h-5" data-sentry-element="Form" data-sentry-component="RiskBandsAdvancedSettings" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
      <Row gutter={[16, 0]} data-sentry-element="Row" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
        {!!notice && <Col span={24} style={{
        marginBottom: "1rem"
      }}>
            <div className="max-w-[80%] pb-2 text-xs text-gray-500">{notice}</div>
          </Col>}
        <Col ref={archetypeRef} span={24} data-sentry-element="Col" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
          <HubFormItem label={<ContextualInfoButtonWithText id="archetypesExplained" drawerWidth={750}>
                {terms("assetArchetype")}
              </ContextualInfoButtonWithText>} data-sentry-element="HubFormItem" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
            <RiskBandsArchetypeSelector archetype={values.archetype || ""} onChange={handleArchetypeChange} data-sentry-element="RiskBandsArchetypeSelector" data-sentry-source-file="RiskBandsAdvancedSettings.tsx" />
          </HubFormItem>
        </Col>

        <Col ref={buildYearRef} span={24} data-sentry-element="Col" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
          <HubFormItem label={<ContextualInfoButtonWithText id="buildYear">{terms("buildYear")}</ContextualInfoButtonWithText>} data-sentry-element="HubFormItem" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
            {/* <InputNumber
              aria-label="Build Year"
              onChange={(value) => setValues({ ...values, buildYear: value || defaultBuildYear })}
              min={inputConstraints.buildYear.min}
              max={inputConstraints.buildYear.max}
              precision={0}
              value={values?.buildYear}
              style={{ width: "100%" }}
             /> */}
            <RiskBandsBuildYearSelector archetype={values.archetype || ""} buildYear={values.buildYear} onChange={handleBuildYearChange} data-sentry-element="RiskBandsBuildYearSelector" data-sentry-source-file="RiskBandsAdvancedSettings.tsx" />
          </HubFormItem>
        </Col>
        <Col ref={heightAboveGroundRef} span={24} data-sentry-element="Col" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
          <HubFormItem label={<ContextualInfoButtonWithText id="heightAboveGround">
                {terms("heightAboveGround")}
              </ContextualInfoButtonWithText>} data-sentry-element="HubFormItem" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
            <RiskBandsHeightAboveGroundSelector archetype={values.archetype || ""} heightAboveGround={values.heightAboveGround} onChange={handleHeightAboveGroundChange} data-sentry-element="RiskBandsHeightAboveGroundSelector" data-sentry-source-file="RiskBandsAdvancedSettings.tsx" />
          </HubFormItem>
        </Col>

        <Col span={24} ref={advancedCollapseRef} data-sentry-element="Col" data-sentry-source-file="RiskBandsAdvancedSettings.tsx">
          <Collapse activeKey={advancedActiveKey} size="small" className="rounded-lg border border-gray-200 bg-surface *:!border-gray-200 [&_.ant-collapse-content]:border-t-0 [&_.ant-collapse-panel]:!border-subtle" expandIconPlacement="start" onChange={toggleAdvancedOpen} items={[{
          key: "advanced-settings",
          label: t("advancedAnalysisSettings"),
          classNames: {
            body: "!pt-1 !border-gray-200 border-t"
          },
          children: <div className="flex flex-col divide-y divide-gray-200 pt-2 *:py-4">
                    <SwitchWithLabel id="anomalyManagementRp" label={t("anomalyManagementRp.title")} description={t("anomalyManagementRp.description")} warning={t("anomalyManagementRp.warningIfFalse")} showWarning={!values.anomalyManagementRp} ref={anomalyManagementRpRef} checked={values.anomalyManagementRp} onChange={(checked: boolean) => {
              setValues({
                ...values,
                anomalyManagementRp: checked
              });
            }} />

                    <SwitchWithLabel id="defences" label={t("defences.title")} description={t("defences.description")} warning={t("defences.warningIfFalse")} showWarning={!values.defences} ref={defencesRef} checked={values.defences} onChange={(checked: boolean) => {
              setValues({
                ...values,
                defences: checked
              });
            }} />
                  </div>
        }]} data-sentry-element="Collapse" data-sentry-source-file="RiskBandsAdvancedSettings.tsx" />
        </Col>
      </Row>
    </Form>;
};